import {
  type InfiniteData,
  type UseQueryResult,
  useInfiniteQuery,
  useQuery
} from '@tanstack/react-query'
import {
  fetchApiWithToken,
  hydraNextPageParam,
  type ResponseError,
  type WithHydraPagination
} from '~/api/utils'
import type {
  ApplicantContactData,
  ApplicantFormation,
  ApplicantInfoExperience,
  ApplicantLanguage,
  ApplicantMatchingData,
  ApplicantMobility,
  ApplicantSoftware
} from '~/domains/applicants/types'
import type { AttachmentResponse } from '~/domains/attachments/types'
import type { TaxonomyItem } from '~/domains/taxonomies/types'
import type { AddressResponse } from '~/types/global'

export type ApplicantInfosResponse = {
  id: number
  lastname: string
  firstname: string
  civility?: string | number
  email?: string
  phoneMobile?: string
  phoneSecondary?: string
  comment?: string | null
  status?: TaxonomyItem<'statuses'> | null
  createdAt?: string
  updatedAt?: string
  qualifications: (TaxonomyItem<'qualifications'> & {
    parent?: TaxonomyItem<'qualifications'> | null
  })[]
  experiences?: ApplicantInfoExperience[]
  formations?: ApplicantFormation[]
  languages?: ApplicantLanguage[]
  softwares?: ApplicantSoftware[]
  mobilities?: ApplicantMobility[]
  address?: AddressResponse | null
  contact?: ApplicantContactData
  resume?: AttachmentResponse | null
} & ApplicantMatchingData

export type ApplicantInfosLDResponse =
  WithHydraPagination<ApplicantInfosResponse>

export const useApplicant = ({
  id,
  showToast = true,
  enabled = true
}: {
  id?: number
  showToast?: boolean
  enabled?: boolean
}): UseQueryResult<
  ApplicantInfosResponse,
  ResponseError<{
    details?: { agencies?: Array<{ id: number; name: string }> }
  }>
> => {
  return useQuery({
    queryKey: ['applicants', id],
    queryFn: async () =>
      await fetchApiWithToken<ApplicantInfosResponse>({
        method: 'get',
        url: `/v1/applicants/${id}`,
        showToast
      }),
    enabled: !!id && enabled
  })
}

type UserHeadData = {
  id: number
  firstname?: string
  lastname: string
  picture?: string
}

export type ApplicantReportSection = {
  name: string
  content: string
}

export type ApplicantReportsResponse = {
  id: number
  title: string
  created: string
  createdBy?: UserHeadData
  modifiedBy?: UserHeadData
  modified?: string
  interviewReportSections: ApplicantReportSection[]
}

export type ApplicantReportsLDResponse =
  WithHydraPagination<ApplicantReportsResponse>

export const useApplicantReports = ({
  applicantId
}: {
  applicantId: number
}): {
  applicantReports: ApplicantReportsResponse[] | undefined
  applicantReportsTotal: number | undefined
  isLoading: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isError: boolean
} => {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError
  } = useInfiniteQuery<
    ApplicantReportsLDResponse,
    Error,
    InfiniteData<ApplicantReportsLDResponse>
  >({
    queryKey: ['applicant', applicantId, 'reports'],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(),
    queryFn: ({ pageParam }) =>
      fetchApiWithToken<ApplicantReportsLDResponse>({
        method: 'get',
        url: `/v1/applicants/${applicantId}/interview_reports?page=${pageParam}&order[created]=desc`,
        useLDJson: true
      }),
    enabled: !!applicantId,
    staleTime: 60_000
  })
  return {
    applicantReports: data?.pages.flatMap(
      (page: ApplicantReportsLDResponse) => page['hydra:member']
    ),
    applicantReportsTotal: data?.pages[0]?.['hydra:totalItems'],
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError
  }
}

export type { ApplicantHeadData } from '~/domains/applicants/types'
